import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { IArquivoRetorno } from '../interfaces/IArquivoRetorno';
import ArquivoRetornoService from '../services/ArquivoRetornoService';

export interface GrupoArquivoRetorno {
    operacaoId: number | null;
    arquivos: IArquivoRetorno[];
    completo: boolean;
    temErro: boolean;
}

/**
 * Agrupa uma lista de ArquivoRetorno pelo operacaoId (todas as partes de um mesmo export
 * compartilham a Operacao que as gerou). Grupos são ordenados do mais recente para o mais
 * antigo (preservando a ordem já retornada pela API); dentro de cada grupo, as partes ficam
 * em ordem crescente de id ("Parte 1", "Parte 2"...).
 *
 * Um grupo é "completo" quando nenhuma parte está processando — sinal suficiente pra liberar
 * o download conjunto, já que o worker só cria a próxima parte quando a anterior termina.
 */
export function agruparArquivosPorOperacao(dados: IArquivoRetorno[]): GrupoArquivoRetorno[] {
    const ordem: (number | null)[] = [];
    const porOperacao = new Map<number | null, IArquivoRetorno[]>();

    for (const arquivo of dados) {
        const chave = arquivo.operacaoId;
        if (!porOperacao.has(chave)) {
            ordem.push(chave);
            porOperacao.set(chave, []);
        }
        porOperacao.get(chave)!.push(arquivo);
    }

    return ordem.map(chave => {
        const arquivos = porOperacao.get(chave)!.slice().sort((a, b) => a.id - b.id);
        return {
            operacaoId: chave,
            arquivos,
            completo: arquivos.every(a => !a.processando),
            temErro: arquivos.some(a => a.erro)
        };
    });
}

/**
 * Baixa todas as partes concluídas de um grupo e as agrupa num único .zip no navegador.
 * Não depende de nenhum processamento no servidor — cada parte já está pronta no GCS,
 * só é buscada e comprimida no cliente.
 */
export async function baixarGrupoComoZip(
    grupo: GrupoArquivoRetorno,
    nomeZip: string,
    onProgress?: (atual: number, total: number) => void
): Promise<void> {
    const partesValidas = grupo.arquivos.filter(a => a.processou && !a.erro);
    if (partesValidas.length === 0) return;

    const zip = new JSZip();

    for (let i = 0; i < partesValidas.length; i++) {
        const arquivo = partesValidas[i];
        const url = await ArquivoRetornoService.obterUrlDownload(arquivo.id);
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Falha ao baixar ${arquivo.nomeArquivo}`);
        const blob = await response.blob();
        zip.file(arquivo.nomeArquivo, blob);
        onProgress?.(i + 1, partesValidas.length);
    }

    const zipBlob = await zip.generateAsync({ type: 'blob' });
    saveAs(zipBlob, nomeZip);
}
